import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft, Plus, MessageSquare, Clock, CheckCircle, 
  AlertCircle, ChevronRight, Send, X, HelpCircle, RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import BottomNav from "@/components/BottomNav";

const CATEGORIES = [
  { id: "account", label: "Account Issues", icon: "👤", desc: "Login, profile, settings" },
  { id: "technical", label: "Technical Problems", icon: "🔧", desc: "Bugs, crashes, errors" },
  { id: "billing", label: "Billing & Payments", icon: "💳", desc: "Subscriptions, coins, refunds" },
  { id: "safety", label: "Safety & Privacy", icon: "🛡️", desc: "Harassment, security concerns" },
  { id: "other", label: "Other", icon: "📝", desc: "General questions" },
];

const STATUS_CONFIG = {
  open: { label: "Open", color: "bg-blue-100 text-blue-700", icon: AlertCircle },
  in_progress: { label: "In Progress", color: "bg-amber-100 text-amber-700", icon: Clock },
  resolved: { label: "Resolved", color: "bg-green-100 text-green-700", icon: CheckCircle },
  closed: { label: "Closed", color: "bg-gray-100 text-gray-700", icon: CheckCircle },
};

const SupportPage = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newTicket, setNewTicket] = useState({ category: "", subject: "", description: "" });
  const [replyText, setReplyText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const response = await api.getTickets();
      setTickets(response.data);
    } catch (error) {
      toast.error("Failed to load tickets");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchTickets();
  };

  const handleCreateTicket = async () => {
    if (!newTicket.category || !newTicket.subject || !newTicket.description) {
      toast.error("Please fill in all fields");
      return;
    }

    setSubmitting(true);
    try {
      await api.createTicket(newTicket);
      toast.success("Ticket created successfully!");
      setShowNewTicket(false);
      setNewTicket({ category: "", subject: "", description: "" });
      fetchTickets();
    } catch (error) {
      toast.error("Failed to create ticket");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async () => {
    if (!replyText.trim()) return;

    setSubmitting(true);
    try {
      await api.replyToTicket(selectedTicket.id, replyText);
      toast.success("Reply sent!");
      setReplyText("");
      const response = await api.getTicket(selectedTicket.id);
      setSelectedTicket(response.data);
      fetchTickets();
    } catch (error) {
      toast.error("Failed to send reply");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenTicket = async (ticket) => {
    try {
      const response = await api.getTicket(ticket.id);
      setSelectedTicket(response.data);
      setTickets(prev => prev.map(t => 
        t.id === ticket.id ? { ...t, has_unread_reply: false } : t
      ));
    } catch (error) {
      toast.error("Failed to load ticket");
    }
  };

  const openCount = tickets.filter(t => t.status === "open" || t.status === "in_progress").length;
  const unreadCount = tickets.filter(t => t.has_unread_reply).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#FAB710]/30 border-t-[#FAB710] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa] pb-24" data-testid="support-page">
      {/* Themed Gradient Header */}
      <div 
        className="relative pt-12 pb-6 px-4 overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #1C3A7A 0%, #0d1f3c 50%, #061022 100%)",
          borderBottomLeftRadius: "25px",
          borderBottomRightRadius: "25px",
        }}
      >
        {/* Decorative Circles */}
        <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-white/[0.08]"></div>
        <div className="absolute bottom-3 -left-5 w-16 h-16 rounded-full bg-white/[0.05]"></div>

        {/* Top Bar */}
        <div className="flex items-center justify-between mb-4">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <button
            onClick={handleRefresh}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            disabled={refreshing}
          >
            <RefreshCw className={`w-5 h-5 text-white ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Centered Icon & Title */}
        <div className="flex flex-col items-center">
          <div 
            className="w-12 h-12 rounded-full flex items-center justify-center mb-2 shadow-lg"
            style={{
              background: "linear-gradient(135deg, #FAB710 0%, #FFD966 100%)",
              boxShadow: "0 4px 8px rgba(250, 183, 16, 0.3)"
            }}
          >
            <HelpCircle className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white text-center drop-shadow-sm">
            Support
          </h1>
          <p className="text-sm text-white/85 mt-1 text-center">
            {openCount} open ticket{openCount !== 1 ? 's' : ''}
            {unreadCount > 0 && (
              <span className="ml-2 text-[#FAB710] font-medium">• {unreadCount} new repl{unreadCount === 1 ? 'y' : 'ies'}</span>
            )}
          </p>
        </div>

        {/* New Ticket Button */}
        <button
          onClick={() => setShowNewTicket(true)}
          className="w-full mt-4 flex items-center justify-center gap-2 bg-[#FAB710] text-white py-3 rounded-xl font-semibold"
          data-testid="new-ticket-btn"
        >
          <Plus className="w-5 h-5" />
          New Ticket
        </button>
      </div>

      {/* Content */}
      <div className="px-4 pt-4">
        {tickets.length === 0 ? (
          <div className="text-center py-12">
            <div 
              className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{
                background: "linear-gradient(135deg, rgba(28, 58, 122, 0.15) 0%, rgba(250, 183, 16, 0.1) 100%)"
              }}
            >
              <MessageSquare className="w-10 h-10 text-[#1C3A7A]" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">No support tickets</h3>
            <p className="text-sm text-gray-500 mb-4">Have a question or issue? Create a ticket!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {tickets.map((ticket) => {
              const statusConfig = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
              const hasUnread = ticket.has_unread_reply;
              return (
                <button
                  key={ticket.id}
                  onClick={() => handleOpenTicket(ticket)}
                  className={`w-full bg-white rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow text-left ${hasUnread ? 'ring-2 ring-[#FAB710]' : ''}`}
                  data-testid={`ticket-${ticket.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusConfig.color}`}>
                          {statusConfig.label}
                        </span>
                        <span className="text-xs text-gray-400">{ticket.ticket_number}</span>
                        {hasUnread && (
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-[#FAB710] text-white animate-pulse">
                            New Reply
                          </span>
                        )}
                      </div>
                      <p className="font-semibold text-gray-900 truncate">{ticket.subject}</p>
                      <p className="text-sm text-gray-500 truncate">{ticket.description}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {format(new Date(ticket.created_at), "MMM d, yyyy 'at' h:mm a")}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* New Ticket Modal */}
      {showNewTicket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
            <div 
              className="px-4 py-3 flex items-center justify-between"
              style={{
                background: "linear-gradient(135deg, #1C3A7A 0%, #0d1f3c 100%)",
              }}
            >
              <h3 className="text-white font-semibold">New Support Ticket</h3>
              <button onClick={() => setShowNewTicket(false)} className="text-white/80 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* Category Selection */}
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <div className="grid grid-cols-2 gap-2 mb-4">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setNewTicket({ ...newTicket, category: cat.id })}
                    className={`p-3 rounded-xl border text-left transition-colors ${
                      newTicket.category === cat.id
                        ? "border-[#FAB710] bg-[#FAB710]/10"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <span className="text-xl">{cat.icon}</span>
                    <p className="font-medium text-gray-900 text-sm mt-1">{cat.label}</p>
                  </button>
                ))}
              </div>

              {/* Subject */}
              <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
              <Input
                value={newTicket.subject}
                onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                placeholder="Brief description of your issue"
                className="mb-4"
              />

              {/* Description */}
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                value={newTicket.description}
                onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                placeholder="Please provide as much detail as possible..."
                className="w-full p-3 border rounded-lg resize-none h-32 focus:ring-2 focus:ring-[#FAB710] focus:border-transparent"
              />

              <Button
                onClick={handleCreateTicket}
                disabled={submitting || !newTicket.category || !newTicket.subject || !newTicket.description}
                className="w-full mt-4 bg-[#FAB710] hover:bg-[#e5a60f]"
              >
                {submitting ? "Creating..." : "Submit Ticket"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
            <div 
              className="px-4 py-3 flex items-center justify-between flex-shrink-0"
              style={{
                background: "linear-gradient(135deg, #1C3A7A 0%, #0d1f3c 100%)",
              }}
            >
              <div>
                <h3 className="text-white font-semibold">{selectedTicket.subject}</h3>
                <p className="text-white/70 text-xs">{selectedTicket.ticket_number}</p>
              </div>
              <button onClick={() => setSelectedTicket(null)} className="text-white/80 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Status & Info */}
            <div className="px-4 py-3 border-b bg-gray-50 flex-shrink-0">
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_CONFIG[selectedTicket.status]?.color}`}>
                  {STATUS_CONFIG[selectedTicket.status]?.label}
                </span>
                <span className="text-xs text-gray-500">
                  {CATEGORIES.find(c => c.id === selectedTicket.category)?.label}
                </span>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Original message */}
              <div className="bg-gray-100 rounded-xl p-3">
                <p className="text-sm text-gray-900">{selectedTicket.description}</p>
                <p className="text-xs text-gray-500 mt-2">
                  {format(new Date(selectedTicket.created_at), "MMM d, yyyy 'at' h:mm a")}
                </p>
              </div>

              {/* Replies */}
              {selectedTicket.replies?.map((reply) => (
                <div
                  key={reply.id}
                  className={`rounded-xl p-3 ${
                    reply.sender_type === "admin"
                      ? "bg-[#1C3A7A] text-white ml-4"
                      : "bg-gray-100 mr-4"
                  }`}
                >
                  <p className={`text-xs font-medium mb-1 ${reply.sender_type === "admin" ? "text-white/70" : "text-gray-500"}`}>
                    {reply.sender_type === "admin" ? "Support Team" : "You"}
                  </p>
                  <p className="text-sm">{reply.message}</p>
                  <p className={`text-xs mt-2 ${reply.sender_type === "admin" ? "text-white/50" : "text-gray-500"}`}>
                    {format(new Date(reply.created_at), "MMM d 'at' h:mm a")}
                  </p>
                </div>
              ))}
            </div>

            {/* Reply Input */}
            {selectedTicket.status !== "closed" && (
              <div className="p-4 border-t bg-white flex-shrink-0">
                <div className="flex gap-2">
                  <Input
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your reply..."
                    className="flex-1"
                    onKeyPress={(e) => e.key === "Enter" && handleReply()}
                  />
                  <Button onClick={handleReply} disabled={!replyText.trim() || submitting} className="bg-[#FAB710]">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default SupportPage;
